import React, { Suspense, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import icon from "../../Images/ogIcon.png";
import "./Home.css";
import Banner from "../../Components/Banner/Banner";
import HomeNewArrivals from "../../Components/HomeNewArrivals/HomeNewArrivals";
import ShopByConcern from "../../Components/ShopByConcern/ShopByConcern";
import MostFavorite from "../../Components/MostFavorite/MostFavorite";
import SwingImage from "../../Components/SwingImage/SwingImage";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import ProductReviews from "../../Components/ProductReviews";
import Achievements from "../Achievements";
import MediaCoverageSectionEBY from "../../Components/Media";
import HomeCicaCare from "../../Components/HomeCicaCare";
import ShopByCategory from "../../Components/ShopByCategory/ShopByCategory";
// Lazy-loaded components
const HomeAbout = React.lazy(() => import("../../Components/HomeAbout"));
const Featured = React.lazy(() => import("../../Components/Featured/Featured"));
const SkinTips = React.lazy(() => import("../../Components/SkinTips"));
const InstaShop = React.lazy(() => import("../../Components/InstaShop"));
const Policy = React.lazy(() => import("../../Components/Policy/Policy"));

dayjs.extend(utc);
dayjs.extend(timezone);

const Home = () => {
  const [showModal, setShowModal] = useState(true);
  // 🔹 Dynamic popup state
  const [popupLoaded, setPopupLoaded] = useState(false);
  const [popup, setPopup] = useState({
    imageUrl: "",
    message: "",
    enabled: false,
    link: "/",
  });

  const currentDate = dayjs().tz("Asia/Dhaka");
  const offerEndTime = dayjs.tz("2025-08-30 23:59:00", "Asia/Dhaka");

  // 🔹 Fetch popup config (relative to same-origin API: /api/popup)
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetch("https://ebay-backend.vercel.app/api/popup");
        const data = await res.json();
        if (!ignore) {
          setPopup({
            imageUrl: data?.imageUrl || "",
            message: data?.message || "",
            enabled: Boolean(data?.enabled),
            link: data?.link || "/", // keep working even if backend has no link field yet
          });
        }
      } catch (e) {
        // swallow errors; just don't show popup
        console.error("Popup fetch failed:", e);
      } finally {
        if (!ignore) setPopupLoaded(true);
      }
    })();
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (showModal && currentDate.isBefore(offerEndTime)) {
      // document.documentElement.classList.add("overflow-hidden");
    } else {
      setShowModal(false);
      // document.documentElement.classList.remove("overflow-hidden");
    }
    // return () => document.documentElement.classList.remove("overflow-hidden");
  }, [showModal]);

  const shouldShowPopup = popupLoaded && popup.enabled && showModal;
  // const shouldShowPopup =
  //   popupLoaded && popup.enabled && showModal && currentDate.isBefore(offerEndTime);

  return (
    <div className="home">
    
      <Helmet>
        {/* ✅ SEO Title (50–60 chars) */}
        <title>Earth Beauty & You | Natural Skin & Hair Care Solutions</title>

        {/* ✅ Meta Description under 160 chars */}
        <meta
          name="description"
          content="Discover eco-friendly skincare & hair care with Earth Beauty & You. Shop organic, sustainable beauty products crafted with natural ingredients."
        />

        {/* ✅ Canonical URL */}
        <link rel="canonical" href="https://earthbeautyandyou.com" />

        {/* ✅ Open Graph for social sharing */}
        <meta
          property="og:title"
          content="Earth Beauty & You – Natural Beauty Solutions"
        />
        <meta
          property="og:description"
          content="Shop eco-friendly & organic beauty products from Earth Beauty & You. Skincare & hair care made with nature’s finest ingredients."
        />
        <meta property="og:image" content={icon} />
        <meta property="og:url" content="https://earthbeautyandyou.com" />
        <meta property="og:type" content="website" />

        {/* ✅ Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Earth Beauty & You – Natural Skincare & Haircare"
        />
        <meta
          name="twitter:description"
          content="Eco-friendly skincare & haircare essentials crafted from nature. Shop consciously with Earth Beauty & You."
        />
        <meta name="twitter:image" content={icon} />

        {/* ✅ JSON-LD Structured Data for SEO */}
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Earth Beauty & You",
            "url": "https://earthbeautyandyou.com",
            "logo": "https://earthbeautyandyou.com/images/logo.png",
            "description": "Discover eco-friendly and organic beauty products for skin & hair care at Earth Beauty & You.",
            "sameAs": [
              "https://www.facebook.com/earthbeautyandyou",
              "https://www.instagram.com/earthbeautyandyou"
            ],
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "16192 Coastal Highway",
              "addressLocality": "Lewes",
              "addressRegion": "Delaware",
              "postalCode": "19958",
              "addressCountry": "US"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+1-800-123-4567",
              "contactType": "Customer Service"
            }
          }
          `}
        </script>
      </Helmet>

      {/* 🔹 Dynamic Modal for Offer */}
      {shouldShowPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[1000]">
          <div className="relative bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm w-full mx-4 animate-fadeIn">
            {/* X Close Button */}
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-1 right-1 text-white w-[30px] h-[30px] text-[10px] font-bold pt-[1px] flex justify-center items-center hover:bg-black transition-colors bg-accent rounded-full"
              aria-label="Close"
            >
              ✕
            </button>

            {/* Offer Image (from API) */}
            <Link to={popup.link || "/"}>
              <img
                alt={
                  popup.message
                    ? `Special Offer: ${popup.message}`
                    : "Special Offer"
                }
                src={
                  popup.imageUrl ||
                  "https://via.placeholder.com/800x800?text=Offer"
                }
                className="rounded-lg w-full object-contain"
              />
            </Link>

            {/* Message (from API) */}
            {popup.message && (
              <h2 className="mt-4 text-lg font-semibold text-gray-800">
                {popup.message}
              </h2>
            )}
          </div>
        </div>
      )}

      {/* ✅ Main SEO Heading */}
      <h1 className="sr-only">
        Earth Beauty & You – Organic Skin & Hair Care Solutions
      </h1>

      {/* ✅ Homepage Sections */}
      <Banner />
      <ShopByCategory></ShopByCategory>
      {/* <SwingImage /> */}
      <HomeNewArrivals />
      <HomeCicaCare/>
      <ShopByConcern />
      <MostFavorite />

      {/* ✅ Lazy load non-critical sections */}
      <Suspense fallback={<div>Loading ...</div>}>
        <HomeAbout />
        <Achievements/>
        {/* <Featured /> */}
        <SkinTips />
        <ProductReviews />
        <MediaCoverageSectionEBY/>
        <InstaShop />
        <Policy />
      </Suspense>
    </div>
  );
};

export default Home;
